import React from 'react';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import Disclosure from 'components/Disclosure';
import Button from 'components/Buttons/Button';
import { EDTEvent } from '@teetsh/app/src/types/edt';
import { getDurationInHoursAndMinutes } from '@teetsh/app/src/utils/time/getDurationInHoursAndMinutes';
import differenceInMinutes from '@teetsh/app/src/utils/time/differenceInMinutes';
import _orderBy from 'lodash-es/orderBy';

interface Props {
  excludedEvents: EDTEvent[];
  onIncludeEvent: (event: EDTEvent) => void;
  className?: string;
}

export function ExcludedEvents({ excludedEvents, onIncludeEvent }: Props) {
  const { t } = useTranslation(['common']);

  if (excludedEvents.length === 0) {
    return null;
  }

  return (
    <div tw="bg-warning-quiet rounded-lg mb-6">
      <Disclosure defaultOpen={false} fill>
        <Disclosure.Button
          alignText="left"
          fill
          tw="px-4 py-2.5 hover:bg-warning-moderate"
          iconRotation="bottom-to-top"
          iconPosition="right"
          small
          minimal
        >
          <div tw="flex items-center gap-1 flex-1">
            <h5 tw="text-warning-intense">
              {t('common:components.tags.excludedFromVolume')}
            </h5>
            <span tw="text-warning-intense font-bold">
              ({excludedEvents.length})
            </span>
          </div>
        </Disclosure.Button>
        <Disclosure.Panel>
          <div tw="px-4 pb-2.5 space-y-2">
            {_orderBy(excludedEvents, ['title'], ['asc']).map(event => {
              const duration = differenceInMinutes(event.end, event.start);
              return (
                <div
                  key={event.id}
                  tw="flex items-center justify-between gap-8"
                >
                  <div tw="flex flex-grow justify-between items-center pr-2 min-w-0">
                    <span tw="text-base text-warning-moderate truncate">
                      {event.title}
                    </span>
                    <div tw="ml-2 text-base text-warning-moderate">
                      {getDurationInHoursAndMinutes(duration, {
                        leadingZero: true,
                        short: true,
                        noMinuteSuffixWhenHours: true,
                      })}
                    </div>
                  </div>
                  <div tw="ml-2">
                    <Button
                      onClick={() => onIncludeEvent(event)}
                      outlined
                      small
                      intent="warning"
                      tw="border-warning-moderate text-warning-intense hover:bg-warning-quiet"
                    >
                      {t('common:buttons.include')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Disclosure.Panel>
      </Disclosure>
    </div>
  );
}
